import { Box, useMediaQuery, useTheme } from "@mui/material";
import { useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { createSwapy } from "swapy";
import { useModals } from "../../context/ModalContext";
import { Can } from "../../permissions/Can";
import { FEATURE_WIDGET_PERMISSIONS } from "../../permissions/configs/widgets/widgets";
import { selectRole } from "../../redux/features/user/selectors/userState.selectors";
import { WorkforceWidget } from "../employees/WorkforceDashboard";
import { FlowDoWidget } from "../flow-do/components/FlowDoWidget/FlowDoWidget";
import { InvoiceRevenueWidget } from "../invoice/DashboardWidget/InvoiceRevenueWidget";
import { JobsCalendarWidget } from "../jobs2/JobsWidgetWithCalendar";
import { QuotesWidget } from "../quotes/QuotesWidget";
// imports para el mockup
import { EProductSection } from "src/commons/interfaces/product";
import { AccessGate } from "../../permissions/access/AccessGate";
import { OverlayBehavior } from "../../permissions/access/behaviors/OverlayBehavior";
import { useBenefitStrategy } from "../../permissions/access/strategies/useBenefitStrategy";
import { SOWWidget } from "../sector-workflow-editor/SOWWidget";
import { AppVersionsWidget } from "../superadmin/appVersions";
import { ContactCenterWidget } from "../superadmin/contact-center";

const WidgetAccessWrapper = ({ widgetId, benefit, requireSubscription, children }) => {
  const benefitsState = useSelector((state) => state.benefits);
  const hasSubscription = Boolean(
    benefitsState?.subscriptions?.subscriptions?.some((item) => item?.subscription?.plan?.product?.section === EProductSection.FLOW_SUBSCRIPTION),
  );
  const benefitStrategy = useBenefitStrategy(benefit);

  const strategy = useMemo(() => {
    if (requireSubscription) {
      return { granted: hasSubscription };
    }
    return benefitStrategy;
  }, [requireSubscription, hasSubscription, benefitStrategy]);

  return (
    <AccessGate
      strategy={strategy}
      behavior={OverlayBehavior}
      behaviorConfig={{ overlayLabel: "Subscribe to unlock", tooltip: "Unlock this widget" }}
    >
      {children}
    </AccessGate>
  );
};

// este componente const layout es para el dashboard principal del negocio, donde se muestran los widgets personalizados según el rol del usuario. Utiliza Swapy para permitir a los usuarios reordenar los widgets mediante drag-and-drop. También muestra un modal de ToolKitDashboard si hay modales relacionados con el dashboard activos y la ruta es "/business". El diseño es responsive, adaptándose a pantallas móviles y de escritorio.
export const HomeLayout = () => {
  const swapy = useRef(null);
  const container = useRef(null);
  const location = useLocation();

  const { modals } = useModals();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const role = useSelector(selectRole);

  // El useEffect se encarga de inicializar Swapy en el contenedor de widgets cuando el componente se monta, pero solo si no es un dispositivo móvil (ya que el drag-and-drop puede no ser ideal en móviles). También se asegura de destruir la instancia de Swapy cuando el componente se desmonta para evitar fugas de memoria.
  // el evento debe detectar el  getWidgetColumnSpan para que al hacer swap y mover un widget a una posición donde deba ocupar 2 columnas, se actualice su grid column span a "span 2", y si se mueve a una posición donde deba ocupar 1 columna, se actualice su grid column span a "span 1". Esto se puede lograr pasando el widgetId en el evento de swap, y luego llamando a getWidgetColumnSpan(widgetId) para obtener el nuevo grid column span del widget, y luego actualizando el estilo del widget correspondiente en el DOM para reflejar el nuevo grid column span.
  useEffect(() => {
    if (isMobile) return;
    if (container.current) {
      swapy.current = createSwapy(container.current);
      swapy.current.onSwap((event) => {
        console.log("swap", event);
        const moveWidgetId = event?.dragging?.item;
        const newColumnSpan = getWidgetColumnSpan(moveWidgetId);
      });
    }
    return () => {
      swapy.current?.destroy();
    };
  }, [isMobile]);

  const dashboardModals = (modals ?? []).filter((modal) => modal?.key?.startsWith("Dashboard"));

  const ALL_WIDGETS = [
    {
      id: "SOWWidget",
      component: <SOWWidget />,
      permissions: FEATURE_WIDGET_PERMISSIONS.SOWWidget,
    },
    {
      id: "AppVersionsWidget",
      component: <AppVersionsWidget />,
      permissions: FEATURE_WIDGET_PERMISSIONS.AppVersionsWidget,
    },
    {
      id: "ContactCenterWidget",
      component: <ContactCenterWidget />,
      permissions: FEATURE_WIDGET_PERMISSIONS.ContactCenterWidget,
    },
    {
      id: "InvoicesWidget",
      component: <InvoiceRevenueWidget />,
      permissions: FEATURE_WIDGET_PERMISSIONS.InvoiceWidget,
      requireSubscription: true,
    },
    {
      id: "QuotesWidget",
      component: <QuotesWidget />,
      permissions: FEATURE_WIDGET_PERMISSIONS.QuotesWidget,
      requireSubscription: true,
      //benefit: EBenefitTypes.QUOTES_BASIC,
    },
    /* mOCKUP 
    {
      id: "MockWidget",
      component: (
        <Box data-swapy-slot="mockWidget">
          <Box
            data-swapy-item="mockWidget"
            sx={{
              cursor: "grab",
              bgcolor: "var(--fx-surface-base, #FFFFFF)",
              borderRadius: "16px",
              padding: "24px",
              display: "flex",
              flexDirection: "column",
              position: "relative",
              border: `1px solid ${colors.gray[100]}`,

              minHeight: "100px",
              overflow: "hidden",
              "&:active": {
                cursor: "grabbing",
              },
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: "50%",
                transform: "translateX(-50%)",
                width: "60px",
                height: "2px",

                borderRadius: "0 0 4px 4px",
                opacity: 0.6,
              }}
            />

            <Box
              sx={{
                position: "absolute",
                top: 12,
                right: 12,
                width: 4,
                height: 4,
                borderRadius: "50%",
                bgcolor: colors.orange[400],
                opacity: 0.4,
                transition: "opacity 0.2s ease",
                "&:hover": {
                  opacity: 0.7,
                },
              }}
            />

            <Box
              sx={{
                p: 3,
                minHeight: 420,
                minWidth: 340,
                display: "flex",
                flexDirection: "column",
                gap: 2,
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography
                    data-widget-title
                    variant="h6"
                    sx={{
                      fontWeight: 500,
                      lineHeight: "normal",
                      justifyContent: "center",
                      fontSize: "1.4rem",
                      color: colors.gray[400],
                      transition: "font-weight 0.15s ease, color 0.15s ease",
                    }}
                  >
                    Contac center
                  </Typography>
                  <Typography fontSize={13} color="text.secondary">
                    Overview of active channels and team response
                  </Typography>
                </Box>

                <IconButton size="small">
                  <MoreVertIcon />
                </IconButton>
              </Box>

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                  gap: 1.5,
                }}
              >
                <Box sx={{ p: 1.5, borderRadius: 3, backgroundColor: "var(--fx-surface-sunken, #F8FAFC)" }}>
                  <Typography fontSize={12} color="text.secondary">
                    Open Conversations
                  </Typography>
                  <Typography fontSize={24} fontWeight={700}>
                    48
                  </Typography>
                </Box>

                <Box sx={{ p: 1.5, borderRadius: 3, backgroundColor: "var(--fx-surface-sunken, #F8FAFC)" }}>
                  <Typography fontSize={12} color="text.secondary">
                    Avg Response
                  </Typography>
                  <Typography fontSize={24} fontWeight={700}>
                    1.2m
                  </Typography>
                </Box>

                <Box sx={{ p: 1.5, borderRadius: 3, backgroundColor: "var(--fx-surface-sunken, #F8FAFC)" }}>
                  <Typography fontSize={12} color="text.secondary">
                    Satisfaction
                  </Typography>
                  <Typography fontSize={24} fontWeight={700}>
                    94%
                  </Typography>
                </Box>
              </Box>

              <Stack spacing={2}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ bgcolor: "var(--fx-surface-muted, #F1F5F9)" }}>
                      <ChatIcon sx={{ color: "#16A34A" }} />
                    </Avatar>
                    <Box>
                      <Typography fontWeight={600}>WhatsApp</Typography>
                      <Typography fontSize={12} color="text.secondary">
                        Fastest response channel
                      </Typography>
                    </Box>
                  </Box>
                  <Typography sx={{ color: "#16A34A", fontWeight: 700 }}>12 pending</Typography>
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ bgcolor: "var(--fx-colors-danger-50, #FEF2F2)" }}>
                      <EmailIcon sx={{ color: "#B42318" }} />
                    </Avatar>
                    <Box>
                      <Typography fontWeight={600}>Gmail</Typography>
                      <Typography fontSize={12} color="text.secondary">
                        Highest ticket volume
                      </Typography>
                    </Box>
                  </Box>
                  <Typography sx={{ color: "#7A4E00", fontWeight: 700 }}>32 tickets</Typography>
                </Box>

                <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ bgcolor: "#FCE7F3" }}>
                      <CameraAltIcon sx={{ color: "#DB2777" }} />
                    </Avatar>
                    <Box>
                      <Typography fontWeight={600}>Instagram</Typography>
                      <Typography fontSize={12} color="text.secondary">
                        Social media follow-ups
                      </Typography>
                    </Box>
                  </Box>
                  <Typography color="text.secondary" fontWeight={600}>
                    0 alerts
                  </Typography>
                </Box>
              </Stack>

              <Box sx={{ borderBottom: "1px solid var(--fx-surface-border, #E2E8F0)" }} />

              <Box
                sx={{
                  mt: "auto",
                  pt: 1,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              />
            </Box>
          </Box>
        </Box>
      ),
      permissions: FEATURE_WIDGET_PERMISSIONS.ClientWidget,
    },
*/
    /*{
      id: "ClientWidget",
      component: <ClientWidget />,
      permissions: FEATURE_WIDGET_PERMISSIONS.ClientWidget,
    },*/

    /*{
    id: "JobsWidget",
     component: <JobsWidget companies={companies} />,
     permissions: FEATURE_WIDGET_PERMISSIONS.JobsWidget,
    }, */

    {
      id: "WorkforceWidget",
      component: <WorkforceWidget />,
      permissions: FEATURE_WIDGET_PERMISSIONS.WorkforceWidget,
      requireSubscription: true,
    },

    /*{
      id: "CalculatorWidget",
      component: <CalculatorWidget />,
      permissions: FEATURE_WIDGET_PERMISSIONS.CalculatorWidget,
    },*/

    /*{
     id: "ConnectionsWiget",
    component: <ConnectionsWidget />,
    permissions: FEATURE_WIDGET_PERMISSIONS.ConnectionsWidget,
    }, */

    {
      id: "JobsCalendarWidget",
      component: <JobsCalendarWidget />,
      permissions: FEATURE_WIDGET_PERMISSIONS.JobsCalendarWidget,
      requireSubscription: true,
    },
    {
      id: "FlowDoWidget",
      component: <FlowDoWidget />,
      permissions: FEATURE_WIDGET_PERMISSIONS.FlowDoWidget,
      requireSubscription: true,
    },
  ];

  const visibleWidgets = ALL_WIDGETS.filter((w) => (role ? w.permissions?.includes(role) : false)); // se filtran los widgets para obtener solo aquellos que el usuario tiene permiso de ver según su rol. Si el rol es nulo o indefinido, no se muestra ningún widget.

  const count = visibleWidgets.length || 1; // se obtiene la cantidad de widgets visibles. Si no hay widgets visibles, se muestra un widget por defecto.

  const desktopCols = Math.min(count, 3); // se determina el número de columnas para el diseño de escritorio, que es el mínimo entre la cantidad de widgets visibles y 3 (ya que el diseño de escritorio tiene un máximo de 3 columnas).

  const getWidgetColumnSpan = (widgetId) => {
    const wideWidgets = ["JobsCalendarWidget"]; // aquí se pentoueden agregar los IDs de los widgets que deben ocupar 2 columnas
    if (wideWidgets.includes(widgetId)) return "span 2";
    return "span 1";
  };

  return (
    <Box sx={{ height: "100%", width: "100%" }} ref={container}>
      <Box
        sx={{
          width: "100%",
          height: "100%",
          display: "grid",
          gap: 3,
          // alignContent: "stretch",
          gridAutoRows: "minmax(0, 1fr)",
          gridAutoFlow: "dense",

          gridTemplateColumns: {
            xs: "minmax(0, 1fr)",
            md: "repeat(3, minmax(0, 1fr))",
          },
        }}
      >
        {visibleWidgets.map((widget) => (
          <Box key={widget.id} sx={{ height: "100%", minWidth: 0, gridColumn: getWidgetColumnSpan(widget.id) }}>
            <Can feature={widget.permissions}>
              {widget.benefit || widget.requireSubscription ? (
                <WidgetAccessWrapper widgetId={widget.id} benefit={widget.benefit} requireSubscription={widget.requireSubscription}>
                  {widget.component}
                </WidgetAccessWrapper>
              ) : (
                widget.component
              )}
            </Can>
          </Box>
        ))}
      </Box>

      {/* {dashboardModals.length > 0 && location.pathname === "/business" && <ToolKitDashboard mode="dashboard" />} */}
    </Box>
  );
};
